import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { annualBreakdown } from '../utils/annualBreakdown';
import { yearRange } from '../utils/replayViewport';
import { glassText } from '../utils/glassSurface';

/**
 * ANNUAL PERFORMANCE, as a collapsible row-per-year inside the glass ledger.
 *
 * <h2>It computes NOTHING of its own</h2>
 *
 * Every figure comes from {@link annualBreakdown}, which mirrors `LedgerStatistics` loop for
 * loop — win is `pnl > 0` strictly, the percentage is COMPOUNDED, profit factor is null with no
 * losses, and each year restarts from a fresh curve. A second implementation in a component is
 * how two panels come to report different numbers for the same trades, which this repository has
 * already paid for once between the Running Ledger and the research dialog.
 *
 * <h2>COLLAPSED by default, and that is about vertical space</h2>
 *
 * The ledger is eleven rows before this, the comparative view ten more, and the dock is a column
 * over a chart. A 33-year run adds thirty-three rows, which would make everything above them
 * something a reader has to scroll past. Collapsed, it is one line; open, it is the answer to
 * "when did this actually work".
 *
 * <h2>Why the years are worth seeing at all</h2>
 *
 * Every band headline in this codebase has turned out to be carried by a handful of trades or a
 * single era — PTA's filtered cohort was +40.54% whole and -10.88% without its best three, and
 * its 2010s were PF 0.51. A per-year row is the cheapest possible version of that check, on
 * screen, while the run is being watched.
 */

/** The years are dense, so they are smaller than the tiles above them but still AA on the glass. */
const HEAD = {
  color: glassText(0.8),
  fontSize: 10,
  letterSpacing: 0.6,
  fontWeight: 700,
  lineHeight: 1.6,
};

const CELL = {
  fontSize: 11,
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1.7,
  whiteSpace: 'nowrap',
};

export const GAIN = '#10B981';
export const LOSS = '#EF4444';

const pct = (v, places = 1) => `${v >= 0 ? '+' : ''}${v.toFixed(places)}%`;

/** "n/a", never a large number: an absent denominator is not a perfect score. */
const pf = (v) => (v === null || v === undefined ? 'n/a' : v.toFixed(2));

/**
 * The five columns, declared once so the header and the rows cannot disagree about their order.
 *
 * <p>`grid` rather than a table: the dock is 389px and a `<table>` sizes itself from its content,
 * so one wide year would push the others out of alignment. Fixed tracks keep every year's digits
 * in the same column, which is the whole point of reading a list of years.
 */
const COLUMNS = '34px 1fr 1fr 1fr 1fr';

export default function ReplayAnnualSummary({ trades, open: initiallyOpen, onFocusYear }) {
  const [open, setOpen] = React.useState(!!initiallyOpen);

  // Computed on every render rather than memoised: it is a single pass over a few hundred
  // trades, and the alternative is a dependency on an array identity that changes per tick
  // anyway. Measured in the same breath as the rest of the HUD, which redraws per bar.
  const { rows } = annualBreakdown(trades);

  // A YEAR WITH NO TRADES IS SIMPLY ABSENT, which falls out of `annualBreakdown` only creating a
  // book for a year it sees. Stated here because the alternative -- a row of dashes for every
  // quiet year in a 33-year run -- is what a naive range-based grouping would produce.
  if (rows.length === 0) return null;

  return (
    <Box data-testid="annual-summary" sx={{ mt: 1.25 }}>
      <Box sx={{ height: 1, background: 'rgba(255,255,255,0.10)', mb: 1 }} />
      <Box
        component="button"
        type="button"
        data-testid="annual-summary-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        sx={{
          ...HEAD,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          width: '100%',
          cursor: 'pointer',
          background: 'transparent',
          border: 0,
          padding: 0,
          textAlign: 'left',
          fontFamily: 'inherit',
        }}
      >
        <Box component="span" sx={{ fontSize: 9, opacity: 0.7 }}>{open ? '▾' : '▸'}</Box>
        ANNUAL PERFORMANCE
        <Box component="span" sx={{ ml: 'auto', opacity: 0.6, fontWeight: 400 }}>
          {`${rows.length} ${rows.length === 1 ? 'year' : 'years'}`}
        </Box>
      </Box>

      {open ? (
        <Box sx={{ mt: 0.75 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: COLUMNS, columnGap: 0.75 }}>
            {['', 'TRADES', 'WIN', 'PF', 'RETURN'].map((h, i) => (
              <Typography
                key={h || 'year'}
                sx={{ ...HEAD, fontSize: 9, textAlign: i === 0 ? 'left' : 'right' }}
              >
                {h}
              </Typography>
            ))}
          </Box>

          {rows.map((r) => (
            <Box
              key={r.year}
              data-testid={`annual-row-${r.year}`}
              /*
               * THE ROW IS THE BUTTON, not a separate icon beside it.
               *
               * A 34px year column and four right-aligned figures leave no room for an affordance
               * that is not the row itself, and the gesture is unambiguous: there is exactly one
               * thing a reader can want from a year they are looking at, which is to see it.
               *
               * A <button> rather than a div with onClick, so it is reachable by keyboard and
               * announced as a control. `display: grid` survives the element change because the
               * columns are declared here; a button's default centring does not, hence the
               * explicit text alignment on each cell below.
               */
              component={onFocusYear ? 'button' : 'div'}
              type={onFocusYear ? 'button' : undefined}
              onClick={onFocusYear ? () => onFocusYear(yearRange(r.year)) : undefined}
              title={onFocusYear ? `Show ${r.year} on the chart` : undefined}
              sx={{
                display: 'grid',
                gridTemplateColumns: COLUMNS,
                columnGap: 0.75,
                width: '100%',
                background: 'transparent',
                border: 0,
                padding: 0,
                fontFamily: 'inherit',
                textAlign: 'left',
                cursor: onFocusYear ? 'pointer' : 'default',
                '&:hover': onFocusYear ? { background: 'rgba(255,255,255,0.06)' } : undefined,
              }}
            >
              <Typography sx={{ ...CELL, color: glassText(0.8), fontWeight: 700 }}>
                {r.year}
              </Typography>
              <Typography sx={{ ...CELL, textAlign: 'right', color: glassText(0.8) }}>
                {r.trades}
              </Typography>
              <Typography sx={{ ...CELL, textAlign: 'right', color: glassText(0.8) }}>
                {`${r.winRatePercent.toFixed(0)}%`}
              </Typography>
              <Typography sx={{ ...CELL, textAlign: 'right', color: glassText(0.8) }}>
                {pf(r.profitFactor)}
              </Typography>
              {/* THE ONLY COLOURED COLUMN. Colouring the win rate or the profit factor too would
                  make the row a wall of green and red with no emphasis anywhere -- and the
                  return is the one a reader is scanning the years for. */}
              <Typography
                sx={{
                  ...CELL,
                  textAlign: 'right',
                  fontWeight: 600,
                  color: r.netPnlPercent > 0 ? GAIN : r.netPnlPercent < 0 ? LOSS : undefined,
                }}
              >
                {pct(r.netPnlPercent)}
              </Typography>
            </Box>
          ))}

          <Typography sx={{ ...HEAD, fontSize: 9, fontWeight: 400, opacity: 0.65, mt: 0.5 }}>
            {/* SAID, because the two are easy to confuse on a sized run: the column is the
                COMPOUNDED gross percentage, the same quantity GROSS RETURN above it reports,
                and not the dollar result in the account block. And each year restarts from a
                fresh curve, so the years neither sum nor compound to the total -- a 2009 row
                carrying 2008's drawdown would be describing the wrong year. */}
            Gross, compounded, per year — each from its own fresh curve, so the years do not sum
            to the total. Trades are grouped by EXIT year; open positions are excluded.
            {onFocusYear ? ' Click a year to take the chart to it; the clock does not move.' : ''}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

ReplayAnnualSummary.propTypes = {
  /** The run's completed trades. Stamped or bare; `annualBreakdown` reads `exitTime`/`pnlPercent`. */
  trades: PropTypes.array,
  /** Start expanded. Off by default — see the note on vertical space. */
  open: PropTypes.bool,
  /** Called with `{from, to}` for the clicked year. Absent leaves the rows inert, not broken. */
  onFocusYear: PropTypes.func,
};
